import { useEffect, useMemo, useState } from 'react'

import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { collectUniqueIds, mapResolvedGameNames, toGameListRequestId } from '../util/overlayResolvedGameNames'
import { useGetGameList } from './gameHooks'

const NAMES_INCLUDE = 'name,names,appId'
const EMPTY_NAMES: Record<string, string> = {}

export const useResolvedGameNames = (gameIds: Array<string | number | undefined | null>, options?: { skip?: boolean }) => {
  const userLanguage = useCurrentUserLanguage()
  const uniqueGameIdsKey = collectUniqueIds(gameIds).join(',')
  const stableGameIds = useMemo(() => (uniqueGameIdsKey ? uniqueGameIdsKey.split(',') : []), [uniqueGameIdsKey])
  const skipByCaller = Boolean(options?.skip) || stableGameIds.length === 0

  const [cached, setCached] = useState<{ language: string; names: Record<string, string> }>(() => ({
    language: userLanguage,
    names: {},
  }))

  const cachedNamesForLanguage = useMemo(() => (cached.language === userLanguage ? cached.names : EMPTY_NAMES), [cached.language, cached.names, userLanguage])
  const unresolvedGameIds = skipByCaller ? [] : stableGameIds.filter((id) => cachedNamesForLanguage[id] === undefined)
  const skipQuery = skipByCaller || unresolvedGameIds.length === 0

  const requestGameIds = useMemo(() => unresolvedGameIds.map(toGameListRequestId), [unresolvedGameIds.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data, isError, isFetching, isSuccess } = useGetGameList({ gameIds: requestGameIds, include: NAMES_INCLUDE }, { skip: skipQuery })
  const mappedNames = useMemo(() => mapResolvedGameNames(data), [data])

  const namesByGameId = useMemo(() => {
    if (!Object.keys(mappedNames).length) {
      return cachedNamesForLanguage
    }

    return { ...cachedNamesForLanguage, ...mappedNames }
  }, [cachedNamesForLanguage, mappedNames])

  useEffect(() => {
    if (!Object.keys(mappedNames).length) {
      return
    }

    setCached((previous) => ({
      language: userLanguage,
      names: { ...(previous.language === userLanguage ? previous.names : {}), ...mappedNames },
    }))
  }, [mappedNames, userLanguage])

  return {
    namesByGameId,
    isLoading: !skipByCaller && Object.keys(namesByGameId).length === 0 && !isError && !isSuccess && (isFetching || !skipQuery),
    isError,
  }
}
